// JobDetail — single role with 6-dim fit breakdown, raw report, CTAs.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Telescope } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import FitScoreCard from '../components/domain/FitScoreCard';
import DeepResearchDrawer from '../components/domain/DeepResearchDrawer';
import LogoSquare from '../components/ui/LogoSquare';
import Chip, { fitVariant } from '../components/ui/Chip';
import { fitTierLabels } from '../lib/fitTiers';
import { apiGet } from '../lib/api';
import { useZh } from '../lib/useProduct';
import { reportNarrative } from '../lib/reportNarrative';
import type { Match } from '../lib/types';

interface MatchResponse {
  item: Match;
}

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const zh = useZh();
  const [researchOpen, setResearchOpen] = useState(false);

  // Fetch exactly this one report. /api/matches is paginated (top-50 by
  // score), so the old "fetch the whole list and Array.find() it" path
  // returned undefined — "Job not found" — for any role past page 1.
  const q = useQuery({
    queryKey: ['match', id],
    queryFn: () => apiGet<MatchResponse>(`/api/matches/${id}`),
    enabled: !!id,
  });

  const job = q.data?.item;

  const reportQ = useQuery({
    queryKey: ['report', job?.report_file],
    queryFn: () =>
      job?.report_file
        ? fetch(`/api/report?file=reports/${encodeURIComponent(job.report_file)}`).then((r) => r.text())
        : Promise.resolve(''),
    enabled: !!job?.report_file,
  });

  if (q.isLoading) return <div className="empty">{zh ? '加载中…' : 'Loading job…'}</div>;
  if (!job) {
    return (
      <div className="page-enter">
        <PageHeader
          eyebrow={zh ? '找不到职位' : 'Job not found'}
          title={zh ? '找不到这个职位。' : "We can't find this role."}
          sub={zh ? '它可能已经被删除了。回到「匹配职位」看看其他的。' : 'Maybe it was removed from reports/. Go back to matches.'}
        />
        <button className="btn" onClick={() => nav('/matches')}>
          {zh ? '← 匹配职位' : '← Matches'}
        </button>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 20 }} onClick={() => nav(-1)}>
        {zh ? '← 返回' : '← Back'}
      </button>
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }}>
        <div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 18 }}>
            <LogoSquare name={job.company} size={56} />
            <div style={{ flex: 1 }}>
              <div className="eyebrow">{job.company}</div>
              <div className="serif" style={{ fontSize: 36, lineHeight: 1.1 }}>
                {job.role}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 6 }}>
                {/* Join only the parts that exist. Interpolating them
                    unconditionally rendered a bare em-dash placeholder and its
                    separators — "— · £28K · Posted …" — on every row whose
                    location the scanner never captured. */}
                {[
                  job.location && job.location !== '—' ? job.location : null,
                  job.comp && job.comp !== '—' ? job.comp : null,
                  job.posted ? (zh ? `发布于 ${job.posted}` : `Posted ${job.posted}`) : null,
                ].filter(Boolean).join(' · ')}
                {job.closes_at && (
                  <>
                    {' · '}
                    <ClosesPill closesAt={job.closes_at} />
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Summary card (Phase 2). Renders at the very top of the detail
              view so a candidate gets the gist before scrolling into the
              full report. Hidden when the report has no role_tldr (older
              reports written before this feature). */}
          {(job.role_tldr || (job.must_haves && job.must_haves.length > 0)) && (
            <div
              className="card"
              style={{
                padding: 20,
                marginBottom: 18,
                borderLeft: '3px solid var(--pri)',
                background: 'rgba(40,120,60,0.04)',
              }}
            >
              {/* Was "Resumen del Rol" — Spanish, left over from the system
                  this project grew out of (modes/oferta.md still writes its
                  blocks in Spanish). Nobody using this product reads Spanish. */}
              <div className="tab" style={{ marginBottom: 8, fontSize: 11 }}>
                {zh ? '岗位速览' : 'Role summary'}
              </div>
              {job.role_tldr && (
                <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink)', marginBottom: 14 }}>
                  {job.role_tldr}
                </div>
              )}
              {job.must_haves && job.must_haves.length > 0 && (
                <div style={{ marginBottom: job.nice_to_haves && job.nice_to_haves.length > 0 ? 14 : 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'var(--ink3)',
                      marginBottom: 6,
                    }}
                  >
                    {zh ? '硬性要求' : 'Must-haves'}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>
                    {job.must_haves.map((r, i) => (
                      <li key={`mh-${i}`}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
              {job.nice_to_haves && job.nice_to_haves.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'var(--ink3)',
                      marginBottom: 6,
                    }}
                  >
                    {zh ? '加分项' : 'Nice-to-haves'}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--ink2)' }}>
                    {job.nice_to_haves.map((r, i) => (
                      <li key={`nh-${i}`}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
            <button className="btn accent" onClick={() => nav('/cv')}>
              {zh ? '定制简历 →' : 'Tailor my CV →'}
            </button>
            {job.url && (
              <a className="btn" href={job.url} target="_blank" rel="noreferrer">
                {zh ? '打开原职位 ↗' : 'Open posting ↗'}
              </a>
            )}
            <button
              className="btn"
              onClick={() => setResearchOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title={zh
                ? '对这家公司做一次深度调研，结果会存下来供面试准备时复用'
                : 'Run a deep research pass on this company — saved to interview-prep/ for reuse'}
            >
              <Telescope size={13} /> {zh ? '公司调研' : 'Deep research'}
            </button>
            <Chip variant={fitVariant(job.fit)}>{fitTierLabels(zh)[job.fit]?.label ?? job.fit}</Chip>
          </div>

          <DeepResearchDrawer
            open={researchOpen}
            company={job.company}
            role={job.role}
            onClose={() => setResearchOpen(false)}
          />

          <div className="card" style={{ padding: 22, marginBottom: 16 }}>
            <div className="tab" style={{ marginBottom: 10 }}>
              {zh ? '评估报告' : 'Report'}
            </div>
            <div
              className="report-md"
              style={{
                fontSize: 13.5,
                lineHeight: 1.65,
                color: 'var(--ink)',
                maxHeight: 620,
                overflowY: 'auto',
              }}
            >
              {reportQ.isLoading && (zh ? '报告加载中…' : 'Loading report…')}
              {reportQ.data ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportNarrative(reportQ.data)}</ReactMarkdown>
              ) : (
                !reportQ.isLoading && (job.report_file
                  ? (zh ? '报告是空的。' : 'Empty.')
                  : (zh ? '这个职位还没有评估报告。' : 'No report for this match yet.'))
              )}
            </div>
          </div>
        </div>

        <div>
          <FitScoreCard score={job.score} breakdown={job.breakdown ?? null} />
          <div className="card" style={{ padding: 22, marginTop: 16 }}>
            <div className="serif" style={{ fontSize: 20, marginBottom: 4 }}>
              {zh ? '投递由你决定' : 'Apply on your terms'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink3)', marginBottom: 16 }}>
              {zh ? '我们绝不会未经你确认就替你投递。' : "We'll never submit without your review."}
            </div>
            {/* Three of the four original entries pointed at /outreach/compose
                and /interview, which are MaintainerRoute — a student clicking
                them got bounced. Presenting dead ends as the page's list of
                next steps is worse than offering fewer steps, so the student
                build lists only what it can actually deliver. */}
            {(zh
              ? [
                  { label: '定制这份简历', to: '/cv' },
                  { label: '看看其他匹配职位', to: '/matches' },
                  { label: '记录到我的申请', to: '/apps' },
                ]
              : [
                  { label: 'Draft tailored CV', to: '/cv/editor' },
                  { label: 'Write cover note', to: '/outreach/compose' },
                  { label: 'Warm intro via LinkedIn', to: '/outreach/compose' },
                  { label: 'Prep interview pack', to: '/interview' },
                ]
            ).map((a, i) => (
              <div
                key={a.label}
                onClick={() => nav(a.to)}
                style={{
                  padding: '10px 0',
                  borderTop: i ? '1px solid var(--line2)' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <span>{a.label}</span>
                <span style={{ color: 'var(--ink3)', fontSize: 11 }}>→</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline application-deadline pill — same color logic as MatchRow's
// ClosesChip but rendered as a span inside the meta line rather than
// a separate chip slot. Hidden when the date is unparseable.
function ClosesPill({ closesAt }: { closesAt: string }) {
  const t = Date.parse(closesAt);
  if (!Number.isFinite(t)) return null;
  const days = Math.floor((t - Date.now()) / (1000 * 60 * 60 * 24));
  const isPast = days < 0;
  const labelDays = Math.abs(days);
  const label = isPast
    ? (labelDays === 0 ? 'Closed today' : `Closed ${labelDays}d ago`)
    : (days === 0 ? 'Closes today' : `Closes in ${days}d`);
  let color = 'var(--ink2)';
  if (!isPast) {
    if (days <= 3) color = '#a13030';
    else if (days <= 7) color = '#8a6f1c';
    else color = '#1f6f3a';
  }
  return (
    <span style={{ color, fontWeight: 500 }} title={`Application deadline: ${closesAt}`}>
      {label}
    </span>
  );
}
